import { TaxItemType, TaxProfile, TaxRate } from "@lemmonhq/types";
import { getAPIClient } from "../helpers";

export interface TaxRateFilters {
  search?: string;
  activeOnly?: boolean;
  limit?: number;
  offset?: number;
}

export interface TaxProfileFilters {
  search?: string;
  limit?: number;
  offset?: number;
}

export interface TaxItemTypeFilters {
  search?: string;
  limit?: number;
  offset?: number;
}

export interface CreateTaxRateData {
  type: string;
  name: string;
  value: number;
  valid_from: string;
  valid_till?: string;
  jurisdiction?: string;
}

export interface CreateTaxProfileData {
  tax_name: string;
  service_method?: string;
  product_item_type?: string;
  jurisdiction?: string;
}

export interface UpdateTaxProfileData {
  tax_name?: string;
  service_method?: string | null;
  product_item_type?: string | null;
  jurisdiction?: string | null;
}

export interface CreateTaxItemTypeData {
  name: string;
  description?: string;
}

export interface UpdateTaxItemTypeData {
  name?: string;
  description?: string | null;
}

export const taxSettingsAPI = {
  // Tax rates
  getTaxRates: async (filters?: TaxRateFilters): Promise<TaxRate[]> => {
    const hc = getAPIClient();
    const params = new URLSearchParams();

    if (filters?.search) params.append("search", filters.search);
    if (filters?.activeOnly !== undefined) {
      params.append("active_only", filters.activeOnly ? "true" : "false");
    }
    if (filters?.limit) params.append("limit", filters.limit.toString());
    if (filters?.offset) params.append("offset", filters.offset.toString());

    const resp = await hc.api.v2.tax_rates.$get({
      query: Object.fromEntries(params.entries()),
    });

    if (!resp.ok) {
      throw new Error("Failed to fetch tax rates");
    }

    return (await resp.json()) as TaxRate[];
  },

  getTaxRate: async (id: string): Promise<TaxRate> => {
    const hc = getAPIClient();
    const resp = await hc.api.v2.tax_rates[":id"].$get({
      param: { id },
    });

    if (!resp.ok) {
      throw new Error("Failed to fetch tax rate");
    }

    return (await resp.json()) as TaxRate;
  },

  createTaxRate: async (data: CreateTaxRateData): Promise<TaxRate> => {
    const hc = getAPIClient();
    const payload = {
      ...data,
      valid_till: data.valid_till ?? undefined,
      jurisdiction: data.jurisdiction ?? undefined,
    };
    const resp = await hc.api.v2.tax_rates.$post({
      json: payload,
    });

    if (!resp.ok) {
      throw new Error("Failed to create tax rate");
    }

    return (await resp.json()) as TaxRate;
  },

  softDeleteTaxRate: async (id: string): Promise<TaxRate> => {
    const hc = getAPIClient();
    const resp = await hc.api.v2.tax_rates[":id"].$delete({
      param: { id },
    });

    if (!resp.ok) {
      throw new Error("Failed to archive tax rate");
    }

    return (await resp.json()) as TaxRate;
  },

  // Tax profiles
  getTaxProfiles: async (
    filters?: TaxProfileFilters,
  ): Promise<TaxProfile[]> => {
    const hc = getAPIClient();
    const params = new URLSearchParams();

    if (filters?.search) params.append("search", filters.search);
    if (filters?.limit) params.append("limit", filters.limit.toString());
    if (filters?.offset) params.append("offset", filters.offset.toString());

    const resp = await hc.api.v2.tax_profiles.$get({
      query: Object.fromEntries(params.entries()),
    });

    if (!resp.ok) {
      throw new Error("Failed to fetch tax profiles");
    }

    return (await resp.json()) as TaxProfile[];
  },

  getTaxProfile: async (id: string): Promise<TaxProfile> => {
    const hc = getAPIClient();
    const resp = await hc.api.v2.tax_profiles[":id"].$get({
      param: { id },
    });

    if (!resp.ok) {
      throw new Error("Failed to fetch tax profile");
    }

    return (await resp.json()) as TaxProfile;
  },

  createTaxProfile: async (data: CreateTaxProfileData): Promise<TaxProfile> => {
    const hc = getAPIClient();
    const payload = {
      tax_name: data.tax_name,
      service_method: data.service_method ?? undefined,
      product_item_type: data.product_item_type ?? undefined,
      jurisdiction: data.jurisdiction ?? undefined,
    };
    const resp = await hc.api.v2.tax_profiles.$post({
      json: payload,
    });

    if (!resp.ok) {
      throw new Error("Failed to create tax profile");
    }

    return (await resp.json()) as TaxProfile;
  },

  updateTaxProfile: async (
    id: string,
    data: UpdateTaxProfileData,
  ): Promise<TaxProfile> => {
    const hc = getAPIClient();
    const resp = await hc.api.v2.tax_profiles[":id"].$patch({
      param: { id },
      json: data,
    });

    if (!resp.ok) {
      throw new Error("Failed to update tax profile");
    }

    return (await resp.json()) as TaxProfile;
  },

  deleteTaxProfile: async (
    id: string,
  ): Promise<{ success: boolean; message?: string }> => {
    const hc = getAPIClient();
    const resp = await hc.api.v2.tax_profiles[":id"].$delete({
      param: { id },
    });

    if (!resp.ok) {
      throw new Error("Failed to delete tax profile");
    }

    return (await resp.json()) as { success: boolean; message?: string };
  },

  // Tax item types
  getTaxItemTypes: async (
    filters?: TaxItemTypeFilters,
  ): Promise<TaxItemType[]> => {
    const hc = getAPIClient();
    const params = new URLSearchParams();

    if (filters?.search) params.append("search", filters.search);
    if (filters?.limit) params.append("limit", filters.limit.toString());
    if (filters?.offset) params.append("offset", filters.offset.toString());

    const resp = await hc.api.v2.tax_item_types.$get({
      query: Object.fromEntries(params.entries()),
    });

    if (!resp.ok) {
      throw new Error("Failed to fetch tax item types");
    }

    return (await resp.json()) as TaxItemType[];
  },

  getTaxItemType: async (id: string): Promise<TaxItemType> => {
    const hc = getAPIClient();
    const resp = await hc.api.v2.tax_item_types[":id"].$get({
      param: { id },
    });

    if (!resp.ok) {
      throw new Error("Failed to fetch tax item type");
    }

    return (await resp.json()) as TaxItemType;
  },

  createTaxItemType: async (
    data: CreateTaxItemTypeData,
  ): Promise<TaxItemType> => {
    const hc = getAPIClient();
    const payload = {
      name: data.name,
      description: data.description ?? undefined,
    };
    const resp = await hc.api.v2.tax_item_types.$post({
      json: payload,
    });

    if (!resp.ok) {
      throw new Error("Failed to create tax item type");
    }

    return (await resp.json()) as TaxItemType;
  },

  updateTaxItemType: async (
    id: string,
    data: UpdateTaxItemTypeData,
  ): Promise<TaxItemType> => {
    const hc = getAPIClient();
    const resp = await hc.api.v2.tax_item_types[":id"].$patch({
      param: { id },
      json: data,
    });

    if (!resp.ok) {
      throw new Error("Failed to update tax item type");
    }

    return (await resp.json()) as TaxItemType;
  },

  deleteTaxItemType: async (
    id: string,
  ): Promise<{ success: boolean; message?: string }> => {
    const hc = getAPIClient();
    const resp = await hc.api.v2.tax_item_types[":id"].$delete({
      param: { id },
    });

    if (!resp.ok) {
      throw new Error("Failed to delete tax item type");
    }

    return (await resp.json()) as { success: boolean; message?: string };
  },
};
